import { useCallback, useMemo } from "react";
import { Popover } from "react-bootstrap";
import reactHtmlParser, { convertNodeToElement } from "react-html-parser";

import { DomElement } from "htmlparser2";
import { Insight } from "models/insight";
import { twMerge } from "tailwind-merge";

import CustomCard from "components/CustomCard";
import EntityAnalyticsChart from "components/EntityAnalyticsChart";
import PremiumServiceWidget from "components/PremiumServiceWidget";
import StickyOverlayTrigger from "components/StickyOverlayTrigger";
import useMediaQuery from "hooks/useMediaQuery";
import { ptSerif, roboto } from "utils/fonts";
import insightStyle from "styles/insightDetails.module.scss";

const RichContent = ({
  html,
  children,
  className,
  hidePreviewModal,
  excludeInsightStyle,
  insight,
}: {
  html?: string;
  children?: React.ReactNode;
  className?: string;
  hidePreviewModal?: boolean;
  excludeInsightStyle?: boolean;
  insight?: Insight;
}) => {
  const { mobile } = useMediaQuery();

  const cleanedHtml = useMemo(() => {
    return html?.replace(/\xa0/g, " ") || ""; // remove &nbsp;
  }, [html]);

  const transform: any = useCallback(
    (node: DomElement, index: number) => {
      if (
        node.type === "tag" &&
        node.attribs?.style?.includes("px!important")
      ) {
        node.attribs.style = node.attribs.style.replace("px!important", "px");
        return convertNodeToElement(node, index, transform);
      }

      if (
        node.type === "tag" &&
        node.name === "premium-service" &&
        insight?.author?.showPremiumServicesTab
      ) {
        return (
          <div className="flex items-center justify-center">
            <CustomCard
              className={twMerge(
                roboto.variable,
                "font-roboto",
                "w-full !shadow-[1px_1px_10px_rgba(0,0,0,0.1)]"
              )}
            >
              <PremiumServiceWidget
                profile={insight?.author}
                isInsightWidget
                insight={insight}
                showCTA
              />
            </CustomCard>
          </div>
        );
      }

      if (node.type === "tag" && node.name === "html") {
        console.log(node);
        node.name = "div";
        return convertNodeToElement(node, index, transform);
      }

      if (hidePreviewModal || mobile) return;

      if (node.type === "tag") {
        const citation = node.attribs?.["data-smartkarma-citation"];
        if (citation) {
          const [type, id] = citation.split(":");
          if (type === "entities") {
            return (
              <StickyOverlayTrigger
                placement="right"
                overlay={
                  <Popover
                    className={twMerge(
                      "left-[-9999px]", // add intitial postion outisde of viewport to fix flickering
                      "bg-white shadow-lg rounded",
                      "min-w-[300px]",
                      "border border-solid border-gray-200",
                      roboto.variable,
                      "font-roboto",
                      "z-[1999]"
                    )}
                  >
                    <Popover.Body>
                      <EntityAnalyticsChart
                        slug={id}
                        showTitle
                        titleClassName="font-bold"
                        priceClassName="leading-[1.4]"
                      />
                    </Popover.Body>
                  </Popover>
                }
              >
                <a
                  {...node.attribs}
                  className={twMerge(node.attribs.className, "inline relative")}
                >
                  {node.children?.map?.(convertNodeToElement)}
                </a>
              </StickyOverlayTrigger>
            );
          }
        }
      }
    },
    [hidePreviewModal, insight, mobile]
  );

  const preprocessNodes = useCallback(
    (nodes: DomElement[]) => {
      let processedNodes = [...nodes];

      if (insight?.detail) {
        if (nodes?.length < 5 && nodes?.length > 1) {
          try {
            const children = nodes.map((node) => node?.children);
            const mostChildren = children.reduce(
              (a, b) => (a?.length > b?.length ? a : b),
              []
            );
            const mostChildrenIndex = children.indexOf(mostChildren);
            const midInsight = Math.ceil(mostChildren.length / 2);

            processedNodes = processedNodes.map((node, index) => {
              if (index === mostChildrenIndex) {
                const children = [...node.children];
                children.splice(midInsight, 0, {
                  type: "tag",
                  name: "premium-service",
                  next: node.children[midInsight],
                  parent: nodes[mostChildrenIndex],
                  prev: node.children[midInsight - 1],
                  attribs: {},
                  children: [],
                });

                return {
                  ...node,
                  children,
                };
              }
              return node;
            });
          } catch (error) {
            console.error("Error processing nodes for premium service:", error);
          }
        } else {
          const midInsight = Math.ceil(nodes.length / 2);
          processedNodes.splice(midInsight, 0, {
            type: "tag",
            name: "premium-service",
            next: processedNodes[midInsight],
            parent: null,
            prev: processedNodes[midInsight - 1],
            attribs: {},
            children: [],
          });
        }
      }

      return processedNodes;
    },
    [insight]
  );

  const content = useMemo(() => {
    if (children) return;

    return reactHtmlParser(cleanedHtml, { transform, preprocessNodes });
  }, [children, cleanedHtml, preprocessNodes, transform]);

  if (children) {
    return (
      <div
        className={twMerge(
          !excludeInsightStyle && insightStyle.container,
          className
        )}
        suppressHydrationWarning
      >
        {children}
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        roboto.variable,
        ptSerif.variable,
        !excludeInsightStyle && insightStyle.container,
        className
      )}
    >
      <div suppressHydrationWarning>{content}</div>
    </div>
  );
};

export default RichContent;
